import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  GAMES_SEARCH_MAX_LENGTH,
  GamesHomeBody,
  SearchForm,
  SearchResultsBody,
  gamesHomeSeo,
} from '@fias/public-site-ui';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { siteOrigin, usePageHead } from '@/lib/document-head';
import { traceEnd, traceError, traceStart } from '@/lib/telemetry';
import { GAMES_SEARCH_MIN_LENGTH, useGamesHome, useGamesSearch } from '../hooks/useGamesCatalog';
import { GamesLoadFailed, GamesLoading } from './GamesStatus';

const RESULTS_ID = 'fg-search-results';

/**
 * The Games home. Search lives here too: typing searches as you go (and keeps
 * `?q=` in the address, so a search can be shared or reloaded); a `?q=` the
 * page is opened with — from the search form of a page without script — runs
 * straight away.
 */
export function GamesHomePage(): ReactElement {
  const [params, setParams] = useSearchParams();
  const urlQuery = (params.get('q') ?? '').slice(0, GAMES_SEARCH_MAX_LENGTH);
  const [text, setText] = useState(urlQuery);
  const debounced = useDebouncedValue(text, 250);
  // The query this page last put in the address itself.
  const lastUrlQuery = useRef(urlQuery);

  // The address follows what is typed (replacing, so Back leaves the page
  // rather than stepping back through every keystroke).
  useEffect(() => {
    const q = debounced.trim();
    if (q === urlQuery.trim()) return;
    lastUrlQuery.current = q;
    setParams(q ? { q } : {}, { replace: true });
    // Only the typed query drives this; the address is read, not watched.
  }, [debounced]);

  // ...and what is typed follows the address when something else changes it
  // (Back/Forward, a link to a search) — never mid-word from its own update.
  useEffect(() => {
    if (urlQuery !== lastUrlQuery.current) {
      lastUrlQuery.current = urlQuery;
      setText(urlQuery);
    }
  }, [urlQuery]);

  const searching = urlQuery.trim().length >= GAMES_SEARCH_MIN_LENGTH;
  const home = useGamesHome();
  const search = useGamesSearch(searching ? urlQuery : '');

  useSearchTrace(searching ? urlQuery.trim() : '', search);

  usePageHead(
    home.data
      ? {
          ...seoFor(home.data),
          // A results page is not a page of its own in search engines.
          noindex: searching || undefined,
        }
      : null,
  );

  const field = (
    <SearchForm
      value={text}
      onChange={setText}
      onSubmit={(value) => {
        setText(value);
        const q = value.trim();
        lastUrlQuery.current = q;
        setParams(q ? { q } : {}, { replace: false });
      }}
      controlsId={RESULTS_ID}
    />
  );

  const results = searching ? (
    <div id={RESULTS_ID}>
      {search.isError ? (
        <GamesLoadFailed what="Search" onRetry={() => void search.refetch()} />
      ) : (
        <SearchResultsBody
          query={urlQuery.trim()}
          games={search.data?.games ?? []}
          pending={!search.data || search.isPlaceholderData}
          genres={home.data?.genres}
        />
      )}
    </div>
  ) : undefined;

  // A failed refresh of data already on screen (a snapshot's, or an earlier
  // answer) keeps it; only a page with nothing to show reports the failure.
  if (home.isError && !home.data && !results) {
    return <GamesLoadFailed what="Games" onRetry={() => void home.refetch()} withTitle />;
  }
  if (!home.data && !results) return <GamesLoading />;
  return (
    <GamesHomeBody
      data={home.data ?? { featured: [], newest: [], top: [], genres: [] }}
      search={field}
      results={results}
    />
  );
}

function seoFor(data: Parameters<typeof gamesHomeSeo>[0]) {
  const seo = gamesHomeSeo(data, siteOrigin() ?? '');
  return {
    title: seo.title,
    description: seo.description,
    canonicalPath: seo.canonicalPath,
    jsonLd: siteOrigin() ? seo.jsonLd : [],
  };
}

/** One telemetry trace per search: how long it took, and how it matched. */
function useSearchTrace(
  query: string,
  search: {
    isFetching: boolean;
    isError: boolean;
    data?: { byMeaning: boolean; games: unknown[] };
  },
): void {
  const trace = useRef<string | null>(null);
  useEffect(() => {
    if (query && search.isFetching && !trace.current) {
      trace.current = traceStart('games.search', { length: query.length });
    }
    if (!search.isFetching && trace.current) {
      if (search.isError) traceError(trace.current, 'Games search failed');
      else
        traceEnd(trace.current, {
          results: search.data?.games.length ?? 0,
          byMeaning: search.data?.byMeaning ?? false,
        });
      trace.current = null;
    }
  }, [query, search.isFetching, search.isError, search.data]);
}
